import constants from '@Common/Core/Constant';
import { documentCategories } from '@Common/Core/DocumentCategories';
import Guid from '@Common/Core/Guid';
import { EntityDriverType, FilterType } from '@Common/Enums';
import { toMultiMap } from '@Common/Helpers/CollectionUtils';
import { DataTimeIntervalHelper } from '@Common/Helpers/DataTimeIntervalHelper';
import documentHelper from '@Common/Helpers/DocumentHelper';
import timeHelper from '@Common/Helpers/TimeHelper';
import {
    AlertMessageWithFields,
    BaseAlertDocument,
    BaseAlertMessage,
    DimensionFilterValues,
    GroupFilter,
    MetricAlertDocument,
    MetricAlertMessage,
    RecordAlertDocument
} from '@InstanceFactory';

export type AlertMessageFilter = (message: BaseAlertMessage) => boolean;

export function getAlertMessageFilter(filters: GroupFilter[], projectId: Guid): AlertMessageFilter {
    const alerts = documentHelper.getDocuments<BaseAlertDocument>(projectId, documentCategories.alert);
    let filteredAlertIds = new Set<Guid>();
    let timeFiltersOperation = [_.stubTrue] as AlertMessageFilter[];
    let filterValuesByFieldId = new Map<string, DimensionFilterValues[]>();

    for (const { entityFilter, dimensionValues } of filters) {
        for (const correctedFilter of correctEntryFilter(entityFilter || {})) {
            const alertsId = getFilterByEntity(correctedFilter)(alerts).map(a => a.id);
            filteredAlertIds = new Set<Guid>([...filteredAlertIds, ...alertsId]);
        }
        const [timeFilters, otherFilters] = _.partition(dimensionValues, isTimeFilter);
        for (const timeFilter of timeFilters) {
            const timeIntervals = timeFilter.values.map(({ id }) => DataTimeIntervalHelper.parse(id));
            const operation = getOperationForFilter(timeFilter);
            const timeOperation = message => operation(timeIntervals, i => timeHelper.dateTimeInInterval(message.moment, i));
            timeFiltersOperation = [...timeFiltersOperation, timeOperation];
        }

        const collection = [...otherFilters, ...filterValuesByFieldId.values()] as DimensionFilterValues[];
        filterValuesByFieldId = toMultiMap(collection);
    }

    const byDimensionsWithFieldsMessage = ({ fields }: Pick<AlertMessageWithFields, 'fields'>) => {
        for (const field of fields) {
            const filterValues = filterValuesByFieldId.get(field.key);
            if (filterValues) {
                for (const fv of filterValues) {
                    const operation = getOperationForFilter(fv);
                    if (!operation(fv.values, v => v.id === field.id)) {
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const byDimensionsMetricMessage = ({ dimension }: Pick<MetricAlertMessage, 'dimension'>) => {
        return byDimensionsWithFieldsMessage({ fields: [dimension] });
    };

    const byEntity = (message: BaseAlertMessage) => filteredAlertIds.size === 0 || filteredAlertIds.has(message.alertId);
    const byTime = (message: BaseAlertMessage) => timeFiltersOperation.every(op => op(message));

    return message => {
        const byDimension: AlertMessageFilter =
            filterValuesByFieldId.size === 0
                ? _.stubTrue
                : MetricAlertMessage.isInstanceOfType(message)
                ? (byDimensionsMetricMessage as any)
                : byDimensionsWithFieldsMessage;
        return [byEntity, byTime, byDimension].every(op => op(message));
    };
}

const getOperationForFilter = ({ filterType }: Pick<DimensionFilterValues, 'filterType'>) =>
    filterType === FilterType.whereIn ? _.some : _.negate(_.some);

const isTimeFilter = (filter: DimensionFilterValues) => timeHelper.isTimeDrillDown(filter.id) || filter.id === constants.dateTimeFilterName;

function* correctEntryFilter({ recordId, metricId }: GroupFilter['entityFilter']): IterableIterator<GroupFilter['entityFilter']> {
    if (Guid.isGuidValue(recordId)) {
        yield { recordId, type: EntityDriverType.record };
    }
    if (Guid.isGuidValue(metricId)) {
        yield { metricId, type: EntityDriverType.metric };
    }
}

function getFilterByEntity(filter: GroupFilter['entityFilter']): (alerts: BaseAlertDocument[]) => BaseAlertDocument[] {
    switch (filter.type) {
        case EntityDriverType.record:
            return alerts => alerts.filter(RecordAlertDocument.isInstanceOfType()).filter(ra => ra.recordId === filter.recordId);
        case EntityDriverType.metric:
            return alerts => alerts.filter(MetricAlertDocument.isInstanceOfType()).filter(ra => ra.settingsId === filter.metricId);
        default:
            return alerts => alerts;
    }
}
